import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { MonthYearPicker } from '../MonthYearPicker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { sarApi } from '../../lib/api';
import { COMMON_FOLLOWUP_REASONS } from '../../lib/followUpReasons';

/**
 * FollowUpDialog — a guided way to ask for what's missing, not a blank box
 * to type an email into. Tick the gaps we noticed and/or a common reason;
 * ticking one reveals a small field beside it for any specific detail.
 * Write anything else in the final field. There's no separate "tidy"
 * step — clicking through always sends everything (ticked reasons, their
 * own request line, any clarification, the free text) to
 * services/ai.compose_follow_up_scope, which weaves it carefully into the
 * request items, before the follow-up SAR is drafted. The letter itself
 * is still built server-side (generate_follow_up_letter) and still
 * reviewed before sending, same as any other SAR.
 *
 * Common reasons come first; the gaps we noticed (each with a long
 * explanation) sit folded underneath, so the dialog stays short. Statutory
 * gaps are still pre-ticked while folded, and the fold says how many.
 *
 * With no request to this organisation in the app (`initialSarId` unset),
 * the records the person uploaded themselves are the response being
 * followed up, so the dialog first asks about that earlier request — how
 * they got the records, roughly when, any reference — and the letter
 * refers to it (`prior_request`, see generate_follow_up_letter).
 * `hasOwnRecords` (they've uploaded something from this organisation)
 * just picks the sensible default answer.
 */
const PRIOR_OPTIONS = [
  { value: 'sar', label: 'I asked them for my records myself', hint: 'A subject access request you made by email, letter or on their website.' },
  { value: 'other', label: 'They gave me my records another way', hint: 'For example through a leaving care worker, or at a meeting.' },
  { value: 'none', label: "I haven't had records from them", hint: "The letter won't mention an earlier request." },
];

const FollowUpDialog = ({ open, onOpenChange, org, gaps = [], initialSarId, hasOwnRecords = false, onCreated }) => {
  const navigate = useNavigate();
  const [pickedGaps, setPickedGaps] = useState(() => new Set());
  const [gapDetails, setGapDetails] = useState({});
  const [pickedReasons, setPickedReasons] = useState(() => new Set());
  const [reasonDetails, setReasonDetails] = useState({});
  const [extra, setExtra] = useState('');
  const [prior, setPrior] = useState({ how: 'none', requested: '', received: '', reference: '' });
  const [gapsOpen, setGapsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const asksAboutPrior = !initialSarId;

  // Reset to a fresh draft each time it's opened, pre-ticking statutory
  // gaps as before — they're the ones with a legal basis to expect.
  useEffect(() => {
    if (!open) return;
    setPickedGaps(new Set(gaps.filter((g) => g.strength === 'statutory').map((g) => g.id)));
    setGapDetails({});
    setPickedReasons(new Set());
    setReasonDetails({});
    setExtra('');
    setGapsOpen(false);
    setPrior({ how: hasOwnRecords ? 'sar' : 'none', requested: '', received: '', reference: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleGap = (id, checked) => {
    setPickedGaps((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleReason = (id, checked) => {
    setPickedReasons((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const nothingPicked = pickedGaps.size === 0 && pickedReasons.size === 0 && !extra.trim();

  const create = async () => {
    if (nothingPicked) { toast.error('Tick something, or write what is missing.'); return; }
    setCreating(true);
    try {
      const reasons = [
        ...gaps.filter((g) => pickedGaps.has(g.id)).map((g) => ({
          id: g.id, label: g.title, request_text: g.request_text,
          clarification: (gapDetails[g.id] || '').trim() || undefined,
        })),
        ...COMMON_FOLLOWUP_REASONS.filter((r) => pickedReasons.has(r.id)).map((r) => ({
          id: r.id, label: r.label, request_text: r.request_text,
          clarification: (reasonDetails[r.id] || '').trim() || undefined,
        })),
      ];
      const composeRes = await sarApi.composeFollowUp(org.id, org.name, reasons, extra);
      const scope = composeRes.data?.items || [];
      if (!scope.length) { toast.error('That did not turn into anything to ask for — try adding a bit more detail.'); return; }

      const res = await sarApi.generate({
        organisation_id: org.id, organisation_name: org.name,
        kind: 'follow_up', parent_sar_id: initialSarId || null, scope, gap_ids: [...pickedGaps],
        prior_request: asksAboutPrior && prior.how !== 'none' ? {
          how: prior.how,
          requested: prior.how === 'sar' ? prior.requested || null : null,
          received: prior.received || null,
          reference: prior.reference.trim() || null,
        } : null,
      });
      toast.success('Follow-up drafted. Read it over, then send.');
      onOpenChange(false);
      onCreated?.();
      navigate(`/sar/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The follow-up could not be created.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle>What's missing?</DialogTitle>
          <DialogDescription>
            Tick why, add any detail, or write your own — we'll put it together carefully. Not a
            fresh copy of everything: they must answer this separately, within a month, and say
            in writing if a record never existed or was destroyed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {asksAboutPrior && (
            <div className="space-y-3" data-testid="followup-prior">
              <div>
                <p className="pd-eyebrow">Your earlier request</p>
                <p className="text-sm text-ink-55 mt-1">
                  So the letter can say what you are following up. Dates and reference are optional.
                </p>
              </div>
              <RadioGroup value={prior.how} onValueChange={(how) => setPrior((p) => ({ ...p, how }))} className="space-y-2">
                {PRIOR_OPTIONS.map((o) => (
                  <label
                    key={o.value}
                    className={`flex items-start gap-3 rounded-lg p-3 cursor-pointer transition-colors duration-state ${
                      prior.how === o.value ? 'bg-brand-wash' : 'bg-ground hover:bg-inset'
                    }`}
                  >
                    <RadioGroupItem value={o.value} className="mt-0.5" data-testid={`followup-prior-${o.value}`} />
                    <div className="space-y-0.5">
                      <p className="font-medium text-ink text-[15px]">{o.label}</p>
                      <p className="text-sm text-ink-55">{o.hint}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
              {prior.how !== 'none' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {prior.how === 'sar' && (
                    <div className="space-y-1.5">
                      <p className="text-sm font-semibold text-ink">When did you ask?</p>
                      <MonthYearPicker
                        value={prior.requested}
                        onChange={(v) => setPrior((p) => ({ ...p, requested: v }))}
                        ariaLabel="When you asked"
                        testId="followup-prior-requested"
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold text-ink">When did you get the records?</p>
                    <MonthYearPicker
                      value={prior.received}
                      onChange={(v) => setPrior((p) => ({ ...p, received: v }))}
                      ariaLabel="When you got the records"
                      testId="followup-prior-received"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="text-sm font-semibold text-ink" htmlFor="followup-prior-reference">Their reference, if they gave one</label>
                    <Input
                      id="followup-prior-reference"
                      value={prior.reference}
                      maxLength={60}
                      onChange={(e) => setPrior((p) => ({ ...p, reference: e.target.value }))}
                      className="max-w-xs"
                      data-testid="followup-prior-reference"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <p className="pd-eyebrow">Common reasons</p>
            {COMMON_FOLLOWUP_REASONS.map((r) => (
              <div key={r.id}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    className="mt-0.5"
                    checked={pickedReasons.has(r.id)}
                    onCheckedChange={(v) => toggleReason(r.id, v)}
                    data-testid={`followup-reason-${r.id}`}
                  />
                  <span className="text-[15px] text-ink-70">{r.label}</span>
                </label>
                {pickedReasons.has(r.id) && (
                  <Input
                    value={reasonDetails[r.id] || ''}
                    onChange={(e) => setReasonDetails((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    placeholder="Any specific detail? (optional)"
                    className="mt-1.5 ml-9 max-w-md"
                    data-testid={`followup-reason-detail-${r.id}`}
                  />
                )}
              </div>
            ))}
          </div>

          {gaps.length > 0 && (
            <div className="rounded-lg bg-ground">
              <button
                type="button"
                onClick={() => setGapsOpen((v) => !v)}
                aria-expanded={gapsOpen}
                className="w-full text-left p-4 flex items-start justify-between gap-3"
                data-testid="followup-gaps-toggle"
              >
                <span>
                  <span className="block text-[15px] font-semibold text-ink">
                    What we noticed in their records ({gaps.length})
                  </span>
                  <span className="block text-sm text-ink-55 mt-0.5">
                    {pickedGaps.size ? `${pickedGaps.size} of ${gaps.length} ticked` : 'None ticked'}
                    {' '}— open to see what each one means.
                  </span>
                </span>
                <ChevronLeft className={`w-4 h-4 mt-1 text-ink-55 shrink-0 transition-transform duration-state ${gapsOpen ? '-rotate-90' : 'rotate-180'}`} aria-hidden="true" />
              </button>
              {gapsOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {gaps.map((g) => (
                    <div key={g.id}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          className="mt-0.5"
                          checked={pickedGaps.has(g.id)}
                          onCheckedChange={(v) => toggleGap(g.id, v)}
                          data-testid={`followup-gap-${g.id}`}
                        />
                        <span className="text-[15px] text-ink-70">
                          <span className="font-semibold text-ink">{g.title}.</span> {g.detail}
                        </span>
                      </label>
                      {pickedGaps.has(g.id) && (
                        <Input
                          value={gapDetails[g.id] || ''}
                          onChange={(e) => setGapDetails((prev) => ({ ...prev, [g.id]: e.target.value }))}
                          placeholder="Any specific detail? (optional)"
                          className="mt-1.5 ml-9 max-w-md"
                          data-testid={`followup-gap-detail-${g.id}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[15px] font-semibold text-ink" htmlFor="followup-extra">
              Anything else, in your own words
            </label>
            <p className="text-sm text-ink-55">
              One thing per line — for example "not sure if I got the unredacted version of the
              June 2012 review". We'll phrase it clearly for the letter without adding anything
              you didn't say.
            </p>
            <Textarea
              id="followup-extra"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={3}
              data-testid="followup-extra"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={creating}>Cancel</Button>
          <Button onClick={create} disabled={creating || nothingPicked} data-testid="followup-create">
            {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}
            Draft the follow-up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FollowUpDialog;
